import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "input", "results", "resultsContainer"]
  
  connect() {
    this.debounceTimer = null
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
  }
  
  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
  }
  
  handleKeydown(e) {
    // Cmd/Ctrl + K to open search
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault()
      this.open()
    }
    // Escape to close
    if (e.key === "Escape" && this.isOpen()) {
      this.close()
    }
  }
  
  isOpen() {
    if (!this.hasOverlayTarget) return false
    return this.overlayTarget.style.display !== 'none'
  }
  
  open() {
    if (!this.hasOverlayTarget || !this.hasInputTarget) return
    this.overlayTarget.style.display = 'block'
    // Trigger animation after display is set
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlayTarget.classList.add('is-open')
      })
    })
    this.inputTarget.focus()
    document.body.classList.add('search-open')
    document.querySelector('.app-navbar')?.style.setProperty('visibility', 'hidden')
    document.querySelector('.app-dock')?.style.setProperty('visibility', 'hidden')
  }
  
  close() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove('is-open')
    // Wait for animation before hiding
    setTimeout(() => {
      this.overlayTarget.style.display = 'none'
      if (this.hasInputTarget) this.inputTarget.value = ""
      if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
      if (this.hasResultsContainerTarget) this.resultsContainerTarget.style.display = 'none'
    }, 300)
    document.body.classList.remove('search-open')
    document.querySelector('.app-navbar')?.style.removeProperty('visibility')
    document.querySelector('.app-dock')?.style.removeProperty('visibility')
  }
  
  preventScroll(event) {
    // Allow scrolling inside results container
    const resultsContainer = this.resultsContainerTarget
    if (!resultsContainer.contains(event.target)) {
      event.preventDefault()
    }
  }
  
  toggle() {
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }
  
  search() {
    if (!this.hasInputTarget || !this.hasResultsContainerTarget) return
    const query = this.inputTarget.value.trim()
    
    if (query.length < 2) {
      this.resultsContainerTarget.style.display = 'none'
      return
    }
    
    // Debounce
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 150)
  }
  
  async fetchResults(query) {
    try {
      const response = await fetch(`/search/suggestions?q=${encodeURIComponent(query)}`)
      const data = await response.json()
      this.renderResults(data.results)
    } catch (error) {
      console.error("Search error:", error)
    }
  }
  
  renderResults(results, query) {
    if (!this.hasResultsTarget || !this.hasResultsContainerTarget) return
    if (results.length === 0) {
      this.resultsTarget.innerHTML = `
        <div style="padding: 32px 16px; text-align: center; color: #666;">
          <i class="fas fa-search" style="font-size: 24px; margin-bottom: 8px; opacity: 0.5; display: block;"></i>
          <p>No results found</p>
        </div>
      `
      this.resultsContainerTarget.style.display = 'block'
      return
    }
    
    const currentQuery = this.inputTarget.value.trim()
    const html = results.map((result, index) => `
      <a href="${result.url}" 
         style="display: flex; align-items: center; gap: 12px; padding: 14px 16px; text-decoration: none; transition: background 0.15s; ${index > 0 ? 'border-top: 1px solid rgba(255,255,255,0.06);' : ''}"
         onmouseover="this.style.background='rgba(255,255,255,0.05)'"
         onmouseout="this.style.background='transparent'"
         data-action="click->global-search#close">
        <div style="width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
          <i class="${result.icon}" style="color: ${this.getIconColor(result.type)}; font-size: 14px;"></i>
        </div>
        <div style="flex: 1; min-width: 0;">
          <p style="margin: 0; font-size: 14px; font-weight: 500; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${this.escapeHtml(result.title)}</p>
          <p style="margin: 4px 0 0; font-size: 12px; color: #888; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${this.escapeHtml(result.subtitle)}</p>
        </div>
        <span style="font-size: 11px; color: #555; text-transform: capitalize; padding: 4px 8px; background: rgba(255,255,255,0.05); border-radius: 4px;">${result.type}</span>
      </a>
    `).join("")
    
    // Add "View all" link
    const viewAllHtml = `
      <a href="/search?q=${encodeURIComponent(currentQuery)}" 
         style="display: block; padding: 12px 16px; text-align: center; color: #60a5fa; font-size: 14px; font-weight: 500; border-top: 1px solid rgba(255,255,255,0.06); text-decoration: none;"
         onmouseover="this.style.background='rgba(255,255,255,0.05)'"
         onmouseout="this.style.background='transparent'">
        View all results →
      </a>
    `
    
    this.resultsTarget.innerHTML = html + viewAllHtml
    this.resultsContainerTarget.style.display = 'block'
  }
  
  getIconColor(type) {
    const colors = {
      post: '#818cf8',      // indigo
      coach: '#60a5fa',     // blue
      service: '#fbbf24',   // amber
      park: '#34d399',      // green
      event: '#a78bfa',     // purple
      route: '#38bdf8',     // sky
      member: '#9ca3af'     // gray
    }
    return colors[type] || '#9ca3af'
  }
  
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
